import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Alert,
  Typography,
  useTheme,
} from '@mui/material';
import AgoraVideoRoom from '../../components/video/AgoraVideoRoom';
import { getAgoraConfigAPI, generateAgoraTokenAPI, getSessionParticipantsAPI } from '../../services/agora';
import { useSelector } from 'react-redux';

const SessionJoin = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const user = useSelector((state) => state.user)?.user;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agoraConfig, setAgoraConfig] = useState(null);
  const [token, setToken] = useState(null);
  const [participants, setParticipants] = useState(null);

  useEffect(() => {
    const initializeSession = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get Agora configuration
        const configResponse = await getAgoraConfigAPI(sessionId);
        const { appId, channelName } = configResponse.data;

        // Generate token with user ID as UID (convert to number for Agora)
        // Use a simple hash of user ID to convert to number
        const userId = user?._id || user?.id;
        let agoraUid = 0;
        if (userId) {
          // Convert MongoDB ObjectId to a number (use last 8 characters as hex, then convert to int)
          const idStr = userId.toString();
          const last8 = idStr.slice(-8);
          agoraUid = parseInt(last8, 16) % 2147483647; // Agora UID max is 2^31 - 1
        }
        const tokenResponse = await generateAgoraTokenAPI(sessionId, agoraUid);
        const { token: agoraToken } = tokenResponse.data;

        // Get session participants
        const participantsResponse = await getSessionParticipantsAPI(sessionId);
        const { participants } = participantsResponse.data;

        setAgoraConfig({ appId, channelName, uid: agoraUid });
        setToken(agoraToken);
        setParticipants(participants);
        setLoading(false);
      } catch (err) {
        console.error('Error initializing session:', err);
        setError(
          err.response?.data?.msg ||
          err.message ||
          'Failed to join session. Please try again.'
        );
        setLoading(false);
      }
    };

    if (sessionId) {
      initializeSession();
    }
  }, [sessionId]);

  const handleLeave = () => {
    navigate(-1); // Go back to previous page
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6">Loading session...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!agoraConfig || !token) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Session configuration not available</Alert>
      </Box>
    );
  }

  return (
    <AgoraVideoRoom
      sessionId={sessionId}
      appId={agoraConfig.appId}
      channelName={agoraConfig.channelName}
      token={token}
      uid={agoraConfig.uid}
      participants={participants}
      currentUserId={user?._id || user?.id}
      whiteboardAppIdentifier={process.env.REACT_APP_AGORA_WHITEBOARD_APP_IDENTIFIER}
      onLeave={handleLeave}
    />
  );
};

export default SessionJoin;

